import React, { useState } from "react";
import { Formik, Form } from "formik";
import { toast } from "react-toastify";
import { API, fileUpload } from "../../../config";

import { Card, CardContent, CardHeader } from "../../..//components/ui/card";
import { Button } from "../../..//components/ui/button";
import { Input } from "../../..//components/ui/input";
import {
  RadioGroup,
  RadioGroupItem,
} from "../../..//components/ui/radio-group";
import { Label } from "../../..//components/ui/label";
import { Loader2 } from "lucide-react";

import QuizResult from "./QuizResult";
import { useOrganization } from "../../../lib/context/OrganisationContext";
import {
  VideoPlayer,
  VideoPlayerContent,
  // VideoPlayerContent,
  VideoPlayerControlBar,
  VideoPlayerFullscreenButton,
  VideoPlayerMuteButton,
  VideoPlayerPlayButton,
  VideoPlayerSeekBackwardButton,
  VideoPlayerSeekForwardButton,
  VideoPlayerTimeDisplay,
  VideoPlayerTimeRange,
  VideoPlayerVolumeRange,
} from "../../../components/ui/video-player";

const Quiz = ({ questionData, setEndQuiz, assessmentQuiz, assessment }) => {
  const [loading, setLoading] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  const { organizationData } = useOrganization();

  const [activeTab, setActiveTab] = useState(assessmentQuiz ? "video" : "quiz"); // default is video

  const initialValues = {
    trainingCoordinator: "",
    answers: questionData.map(() => ""),
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("training_id", questionData[0].training_id);
      formData.append("organization_id", organizationData?.organizationId);

      if (assessmentQuiz) {
        formData.append(
          "training_coordinator",
          values.trainingCoordinator || "",
        );
      }

      questionData.forEach((q, i) => {
        formData.append(`questions[${i}][question_id]`, q.id);
        formData.append(`questions[${i}][answer]`, values.answers[i]);
        formData.append(`questions[${i}][correct_answer]`, q.answer);
      });

      const { data } = await fileUpload(API.CREATE_TRAINING_QUIZ, formData);

      setQuizResult(data.data);
      setEndQuiz(true);
      toast.success(data.message);
    } catch (e) {
      const errors = e.response?.data?.errors || {};
      Object.values(errors).forEach((err) => toast.error(err[0]));
    } finally {
      setLoading(false);
    }
  };

  if (quizResult) {
    const enrichedQuestions = questionData?.map((q, i) => ({
      ...q,
      selectedAnswer: quizResult.answers?.[i]?.answer ?? null,
      isCorrect:
        quizResult.answers?.[i]?.correct_answer ===
        quizResult.answers?.[i]?.answer,
    }));

    return (
      <QuizResult
        result={quizResult}
        questions={enrichedQuestions}
        onRetake={() => setQuizResult(null)}
      />
    );
  }

  return (
    <>
      <div
        className={`grid grid-cols-1 ${assessmentQuiz ? "lg:grid-cols-12" : ""} gap-4 p-4`}
      >
        {/* LEFT BUTTONS PANEL */}
        {assessmentQuiz ? (
          <aside className="xl:col-span-2 lg:col-span-3 space-y-4">
            <Card className="p-4 lg:space-y-3 sticky top-4 lg:block flex items-center gap-4">
              <Button
                variant={activeTab === "video" ? "default" : "outline"}
                className="w-full"
                onClick={() => setActiveTab("video")}
              >
                🎥 Video
              </Button>

              <Button
                variant={activeTab === "quiz" ? "default" : "outline"}
                className="w-full"
                onClick={() => setActiveTab("quiz")}
              >
                📝 Quiz
              </Button>
            </Card>
          </aside>
        ) : (
          <></>
        )}
        <main className="xl:col-span-10 lg:col-span-9">
          {activeTab === "video" && (
            <VideoPlayer className="overflow-hidden rounded-lg border aspect-video w-full">
              <VideoPlayerContent
                crossOrigin=""
                muted
                preload="auto"
                slot="media"
                // src="https://youtu.be/yjCBX0GgECM?si=Je_KRK3mDN1ARkDT"
                src="https://youtu.be/I_gqtGmSmfo"
              />
              <VideoPlayerControlBar>
                <VideoPlayerPlayButton />
                <VideoPlayerSeekBackwardButton />
                <VideoPlayerSeekForwardButton />
                <VideoPlayerTimeRange />
                <VideoPlayerTimeDisplay
                  showDuration
                  className="text-webPrimary"
                />
                <VideoPlayerMuteButton />
                <VideoPlayerVolumeRange />
                <VideoPlayerFullscreenButton />
              </VideoPlayerControlBar>
            </VideoPlayer>
          )}

          {activeTab === "quiz" && (
            <div className="rounded-lg border p-4">
              <Formik initialValues={initialValues} onSubmit={handleSubmit}>
                {({ values, setFieldValue }) => {
                  const allAnswered = values.answers.every(Boolean);

                  return (
                    <Form className="space-y-6">
                      <h6 className="text-lg font-semibold">
                        {assessmentQuiz ? assessment?.title : "Quiz"}
                      </h6>

                      {assessmentQuiz && (
                        <p className="text-muted-foreground">
                          {assessment?.description}
                        </p>
                      )}

                      {assessmentQuiz && (
                        <div className="space-y-1">
                          <Label>Training Coordinator</Label>
                          <Input
                            value={values.trainingCoordinator}
                            onChange={(e) =>
                              setFieldValue(
                                "trainingCoordinator",
                                e.target.value,
                              )
                            }
                            placeholder="Enter training coordinator name"
                          />
                        </div>
                      )}

                      {questionData.map((q, qi) => (
                        <Card key={q.id}>
                          <CardHeader>
                            <div className="text-base font-bold">
                              {q.subtopic && <b>{q.subtopic}</b>} {q.question}
                            </div>
                          </CardHeader>

                          <CardContent>
                            <RadioGroup
                              value={values.answers[qi]}
                              onValueChange={(val) =>
                                setFieldValue(`answers.${qi}`, val)
                              }
                              className="space-y-2"
                            >
                              {q.type === "0"
                                ? ["Yes", "No"].map((opt) => (
                                    <div
                                      key={opt}
                                      className="flex items-center gap-2"
                                    >
                                      <RadioGroupItem
                                        value={opt}
                                        id={`${qi}-${opt}`}
                                      />
                                      <Label htmlFor={`${qi}-${opt}`}>
                                        {opt}
                                      </Label>
                                    </div>
                                  ))
                                : q.options.map((opt) => (
                                    <div
                                      key={opt.label}
                                      className="flex items-center gap-2"
                                    >
                                      <RadioGroupItem
                                        value={opt.label}
                                        id={`${qi}-${opt.label}`}
                                      />
                                      <Label htmlFor={`${qi}-${opt.label}`}>
                                        {opt.label} - {opt.text}
                                      </Label>
                                    </div>
                                  ))}
                            </RadioGroup>
                          </CardContent>
                        </Card>
                      ))}

                      <div className="flex justify-end">
                        <Button
                          type="submit"
                          disabled={!allAnswered || loading}
                        >
                          {loading && (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          )}
                          Submit
                        </Button>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default Quiz;
